import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getResourcePage } from '../../content/resources/metadata';

const SITE_URL = 'https://www.prepbox.io';

const pages = {
    '/': {
        title: 'Math Studio',
        description:
            'Together, Math can be Fun. PrepBox reveals the connected understanding beneath your child’s math marks through handwritten work, an educator-built MathGraph, and clear next steps.',
    },
    '/trial': {
        title: 'Create Your Free Math Studio Account | PrepBox',
        description: 'One free account: your child’s first two sessions are free, with Uni’s help on their page. Automatic marking, XP and independent practice stay free after that. No card, no subscription.',
    },
    '/k6program': {
        title: 'K–6 Math Tutoring: Fluency and Mental Models | PrepBox',
        description:
            'K–6 math tutoring that combines number fluency, visual models, and real handwritten problem solving so understanding lasts when problems change.',
    },
    '/tutoring': {
        title: 'Online Math Tutoring for Grades 1–12 | PrepBox',
        description:
            'Personalized online math tutoring for Grades 1–12 with real-time handwritten work, expert guidance, and a connected curriculum map.',
    },
    '/about': {
        title: 'About Min Lee and PrepBox Math Tutoring',
        description:
            'Meet Min Lee and learn why PrepBox measures how students think, not only whether they reached the right answer.',
    },
    '/lessons': {
        title: 'Math Video Lessons and Guided Practice | PrepBox',
        description:
            'Explore curriculum-grounded math lessons that connect visual models, mathematical reasoning, and guided handwritten practice.',
    },
    '/workbooks': {
        title: 'Free Math Workbooks With Video Solutions | PrepBox',
        description:
            'Download free math workbooks with step-by-step video solutions for Canadian and US curricula.',
    },
    '/worksheets': {
        title: 'Free Math Worksheets With Video Solutions | PrepBox',
        description:
            'Explore free math worksheets with step-by-step video solutions and curriculum-aligned practice.',
    },
    '/blog': {
        title: 'Mental Models, Math Learning, and Teaching | PrepBox Blog',
        description:
            'Essays from math educator Min Lee on mental models, mathematical fluency, connected thinking, and what real understanding looks like.',
    },
};

export const getSitePageMetadata = (pathname) => {
    const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
    return getResourcePage(path) || pages[path];
};

const resourceFallback = (path) => {
    const match = path.match(/^\/(lessons|workbooks|worksheets)\/([^/]+)(\/.*)?$/);
    if (!match) return null;
    const canonicalPath = path.replace(/^\/worksheets(?=\/)/, '/workbooks');
    const parent = getResourcePage(`/${match[1]}/${match[2]}`);
    return parent && match[3]
        ? {
            title: `Math Practice: ${parent.resource.name} | PrepBox`,
            description: parent.description,
            canonical: `${SITE_URL}${canonicalPath}`,
            robots: 'noindex, follow',
        }
        : {
            title: 'Math Resource Not Found | PrepBox',
            description: 'Browse PrepBox’s free math workbook and lesson libraries to find an available resource.',
            canonical: `${SITE_URL}${canonicalPath}`,
            robots: 'noindex, follow',
        };
};

const SiteSeo = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const normalizedPath = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
        const resourcePage = getResourcePage(normalizedPath);
        const page = getSitePageMetadata(normalizedPath) || resourceFallback(normalizedPath);

        if (!resourcePage) removeJsonLd('resource-schema');

        // Blog articles own their more specific metadata and Article schema.
        if (!page || normalizedPath.startsWith('/blog/')) return;

        const canonical = page.canonical || `${SITE_URL}${normalizedPath === '/' ? '/' : normalizedPath}`;

        document.title = page.title;
        setMeta('name', 'description', page.description);
        setMeta('property', 'og:title', page.title);
        setMeta('property', 'og:description', page.description);
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:url', canonical);
        setMeta('name', 'twitter:title', page.title);
        setMeta('name', 'twitter:description', page.description);
        // Cover images belong to the current resource, not the previous route.
        const coverUrl = resourcePage?.resource?.coverUrl;
        ['og:image', 'og:image:alt', 'og:image:width', 'og:image:height'].forEach((name) => {
            document.querySelectorAll(`meta[property="${name}"]`).forEach((tag) => tag.remove());
        });
        ['twitter:image', 'twitter:image:alt'].forEach((name) => {
            document.querySelectorAll(`meta[name="${name}"]`).forEach((tag) => tag.remove());
        });
        if (coverUrl) {
            setMeta('property', 'og:image', coverUrl);
            setMeta('property', 'og:image:alt', resourcePage.h1);
            setMeta('name', 'twitter:image', coverUrl);
            setMeta('name', 'twitter:image:alt', resourcePage.h1);
        }
        setCanonical(canonical);
        setMeta('name', 'robots', page.robots || 'index, follow');
        removeJsonLd('article-schema');
        removeJsonLd('prerender-schema');

        if (resourcePage) {
            setJsonLd('resource-schema', {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: resourcePage.breadcrumbs.map((crumb, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    name: crumb.label,
                    item: `${SITE_URL}${crumb.path}`,
                })),
            });
        }

        if (normalizedPath === '/') {
            setJsonLd('site-schema', {
                '@context': 'https://schema.org',
                '@graph': [
                    {
                        '@type': 'EducationalOrganization',
                        '@id': `${SITE_URL}/#organization`,
                        name: 'PrepBox',
                        url: `${SITE_URL}/`,
                        founder: {
                            '@type': 'Person',
                            name: 'Min Lee',
                        },
                        email: 'hello@prepanywhere.com',
                    },
                    {
                        '@type': 'WebSite',
                        '@id': `${SITE_URL}/#website`,
                        url: `${SITE_URL}/`,
                        name: 'PrepBox',
                        publisher: { '@id': `${SITE_URL}/#organization` },
                    },
                ],
            });
        } else {
            removeJsonLd('site-schema');
        }
    }, [pathname]);

    return null;
};

const setMeta = (attr, value, content) => {
    const tags = Array.from(document.querySelectorAll(`meta[${attr}="${value}"]`));
    let tag = tags.shift();
    tags.forEach((duplicate) => duplicate.remove());
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, value);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
};

const setCanonical = (href) => {
    const links = Array.from(document.querySelectorAll('link[rel="canonical"]'));
    let canonical = links.shift();
    links.forEach((duplicate) => duplicate.remove());
    if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', href);
};

export const setJsonLd = (id, value) => {
    let script = document.getElementById(id);
    if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(value);
};

export const removeJsonLd = (id) => {
    document.getElementById(id)?.remove();
};

export { SITE_URL, setCanonical, setMeta };
export default SiteSeo;
